import { DateTime, Interval } from 'luxon';
import { BookingSlot, ResourceBooking, UserBooking } from '../types/booking';
import { ApiIloqBookingSchemaDay } from '@allbin/viu-api-client';

/**
 * Maps slots from ApiIloqBookingSchema to the internal BookingSlot format
 * and fills them with booking status.
 */
export function mapAndFillApiSlots(
  iloqSchemaDays: ApiIloqBookingSchemaDay[] | undefined,
  bookings: ResourceBooking[],
  resourceUserBookings: UserBooking[],
): BookingSlot[] {
  if (!iloqSchemaDays) {
    return [];
  }

  const now = DateTime.now();
  const allMappedSlots: BookingSlot[] = [];

  // Create a Set of the current user's booking IDs for efficient lookup
  const userBookingIds = new Set(resourceUserBookings.map((b) => b.id));

  // Pre-parse booking intervals for efficient overlap checks
  const bookingIntervals = bookings
    .map((booking) => {
      const start = DateTime.fromISO(booking.start);
      const end = DateTime.fromISO(booking.end);
      if (start.isValid && end.isValid) {
        return {
          interval: Interval.fromDateTimes(start, end),
          id: booking.id,
          bookedBy: booking.bookedBy,
        };
      }
      return null;
    })
    .filter((b) => b !== null) as {
    interval: Interval;
    id: string;
    bookedBy: string | undefined;
  }[]; // Type assertion after filter

  iloqSchemaDays.forEach((day) => {
    day.slots.forEach((apiSlot) => {
      const slotStartTime = apiSlot.start_time;
      const slotEndTime = apiSlot.end_time;

      if (!slotStartTime || !slotEndTime) {
        console.warn(
          `[mapAndFillApiSlots] Missing start_time or end_time for slot:`,
          apiSlot,
          day.date,
        );
        return; // Skip slots with missing times
      }

      const slotStart = DateTime.fromISO(`${day.date}T${slotStartTime}`);
      const slotEnd = DateTime.fromISO(`${day.date}T${slotEndTime}`);
      const startISO = slotStart.toISO();
      const endISO = slotEnd.toISO();

      if (!startISO || !endISO || !slotStart.isValid || !slotEnd.isValid) {
        console.warn(
          `[mapAndFillApiSlots] Could not parse date/time for slot:`,
          apiSlot,
          day.date,
        );
        return;
      }

      let status: BookingSlot['status'] = 'available';
      let isAvailable = apiSlot.bookable;
      let associatedBookingId: string | undefined = undefined;
      let slotBookedBy: string | undefined = undefined;

      const isPast = slotEnd < now;

      if (isPast) {
        status = 'past';
        isAvailable = false;
      } else {
        // Check for overlaps with existing bookings
        const slotInterval = Interval.fromDateTimes(slotStart, slotEnd);
        const overlappingBooking = bookingIntervals.find((b) =>
          b.interval.overlaps(slotInterval),
        );

        if (overlappingBooking) {
          // Log the values being compared
          // console.log(`[mapAndFillApiSlots] Comparing for user booking: booking.id="${overlappingBooking.id}", userBookingIds size=${userBookingIds.size}`); // Keep for debugging if needed

          // Simplified status logic: Check ONLY if the overlapping booking ID is in the user's booking list.
          // The userBookingIds set will only be populated if the user is logged in and useUserBookings has run.
          if (userBookingIds.has(overlappingBooking.id)) {
            status = 'userBooked';
          } else {
            status = 'taken'; // Otherwise, it's just taken by someone else.
          }

          // Original logic (relied on potentially out-of-sync userNfcId prop):
          // if (userNfcId && userBookingIds.has(overlappingBooking.id)) {
          //    status = 'userBooked';
          // } else {
          //    status = 'taken';
          // }
          isAvailable = false;
          associatedBookingId = overlappingBooking.id;
          // bookedBy can potentially be derived from userBookings list if needed later,
          // but for status check, just knowing if it IS a user booking is enough.
          slotBookedBy = userBookingIds.has(overlappingBooking.id)
            ? 'user'
            : 'other'; // Simplified indicator
        } else if (!apiSlot.bookable) {
          // If API explicitly says not bookable, and not past, and not overlapping
          status = 'taken'; // Or perhaps a new status like 'unavailable'?
          isAvailable = false;
        }
        // If none of the above, it remains 'available' as initialized
      }

      // WORKAROUND: Accessing slot_id via snake_case due to API/Type mismatch.
      // TODO: Align API response and/or client types and revert.
      const slotId = apiSlot.slot_id;
      if (!slotId) {
        console.warn('[mapAndFillApiSlots] Slot missing ID:', apiSlot);
        // Decide how to handle slots without ID, maybe skip?
        // return;
      }

      allMappedSlots.push({
        id: slotId || `unknown-${startISO}`, // Use snake_case ID, fallback if missing
        start: startISO,
        end: endISO,
        isAvailable: isAvailable,
        status: status,
        bookingId: associatedBookingId,
        bookedBy: slotBookedBy,
      });
    });
  });

  // console.log(`[mapAndFillApiSlots] Output mapped slots count: ${allMappedSlots.length}`); // REMOVED LOG
  // console.log('[mapAndFillApiSlots] Output mapped slots (first 10):', allMappedSlots.slice(0, 10)); // REMOVED LOG
  return allMappedSlots;
}

/**
 * Groups the mapped and filled slots by day.
 */
export function groupApiSlotsByDay(
  mappedSlots: BookingSlot[],
): Map<string, BookingSlot[]> {
  const days = new Map<string, BookingSlot[]>();
  mappedSlots.forEach((slot) => {
    const day = DateTime.fromISO(slot.start).toISODate();
    if (!day) return;
    const daySlots = days.get(day) || [];
    days.set(day, [...daySlots, slot]);
  });
  // Logga första dagen i mappen för att se hur den ser ut
  // const firstKey = days.keys().next().value; // REMOVED
  //  if (firstKey) { // REMOVED LOG BLOCK
  //    console.log(`[groupApiSlotsByDay] Output map entry for ${firstKey} (first 10 slots):`, (days.get(firstKey) || []).slice(0, 10));
  //  }
  return days;
}
